import {PhaAccountValuation, PhaAssetType, PhaOptionType, PhaRowOrigin, PhaSheet, PhaSide} from '../pha-sheet.model';
import {PhaSectionSettings, sectionSettings} from '../pha-section-settings';
import {PhaStrategyLeg} from '../pha-option-strategy';

// THE SHEET STATE (CR-20260930-001 M2): what the session saves so the design comes back exactly as
// it was on restore (combo switch and back, panel reopen, login — §4 "Restore", §5 "Durability").
// Accounting cash/transfer references are saved; live prices and derived account values are not.
// Pure TypeScript: serialize here; the panel applies a restore because it needs the chains and the
// quote subscriptions. Only what the trader set is saved — prices, chains, quotes, derived numbers
// are not (they come from the feed again). Versioned so a later shape can read an older one.

export const PHA_SHEET_STATE_VERSION = 11;

export interface PhaUnderlyingState {
    ticker: string;
    displayName: string;
    origin: PhaRowOrigin;
    side: PhaSide;
    shares: number;
    pctOverride: number | null;
    sharesLock: boolean;
    pctLock: boolean;
    crossedOut: boolean;
    sourcePositionId?: string;
    assetType?: PhaAssetType;
    callUnderlying?: string;
    callStrike?: number;
    callExpirationDate?: string;
    callExpirationTicker?: string;
}

export interface PhaOptionRowState extends PhaStrategyLeg {
    optionTicker?: string;
    /** Premium: the underlying ticker (pooled) or the index ticker; Protection: the symbol. */
    ticker: string;
    displayName: string;
    /** Premium only: false = an index row (no pool). */
    pooled?: boolean;
    pooledUnderlyingTicker?: string;
    optionType: PhaOptionType;
    side: PhaSide;
    origin: PhaRowOrigin;
    removed: boolean;
    /** yyyy-MM-dd of the picked expiration, null when none. */
    expirationDate: string | null;
    /** The option root of the picked expiration (SPX vs SPXW), to re-pick the same listing. */
    expirationTicker: string | null;
    strike: number | null;
    contracts: number;
    /** Premium only: what the book held on a held row at import (the pool never cuts below it). Absent in older states = 0. */
    heldContracts?: number;
    otmLock: boolean;
}

export interface PhaSheetState {
    version: number;
    accountValue?: number | null;
    accountValuation?: PhaAccountValuation;
    /** Legacy session field, read only when restoring pre-v2 designs. */
    accountSize?: number | null;
    stockAllocationPercent?: number;
    roundingDeviationPercent?: number;
    sectionSettings?: {premium: PhaSectionSettings, protection: PhaSectionSettings};
    underlyings: PhaUnderlyingState[];
    premium: PhaOptionRowState[];
    protection: PhaOptionRowState[];
}

export function serializeSheet(sheet: PhaSheet, settings?: {premium: PhaSectionSettings, protection: PhaSectionSettings}): PhaSheetState {
    return {
        version: PHA_SHEET_STATE_VERSION,
        accountValue: sheet.accountValue,
        accountValuation: sheet.accountValuation,
        stockAllocationPercent: sheet.stockAllocationPercent,
        roundingDeviationPercent: sheet.roundingDeviationPercent,
        sectionSettings: {
            premium: sectionSettings(settings && settings.premium),
            protection: sectionSettings(settings && settings.protection)
        },
        underlyings: sheet.rows.map(r => ({
            ticker: r.ticker,
            displayName: r.displayName,
            origin: r.origin,
            side: r.side,
            shares: r.shares,
            pctOverride: r.pctOverride,
            sharesLock: r.sharesLock,
            pctLock: r.pctLock,
            crossedOut: r.crossedOut,
            sourcePositionId: r.sourcePositionId,
            assetType: r.assetType,
            callUnderlying: r.callUnderlying,
            callStrike: r.callStrike,
            callExpirationDate: r.callExpirationDate,
            callExpirationTicker: r.callExpirationTicker
        })),
        premium: sheet.premiumRows.map(p => ({
            strategyId: p.strategyId, strategyUseAdjacent: p.strategyUseAdjacent, strategyFamilyId: p.strategyFamilyId, strategyType: p.strategyType, strategyLeg: p.strategyLeg,
            strategyGap: p.strategyGap, expectedStrike: p.expectedStrike,
            ticker: p.ticker,
            optionTicker: p.optionTicker,
            displayName: p.displayName,
            pooled: p.pooled,
            pooledUnderlyingTicker: p.pooled ? (sheet.rows.find(r => r.id === p.underlyingRowId) || {ticker: undefined}).ticker : undefined,
            optionType: p.optionType,
            side: p.side,
            origin: p.origin,
            removed: p.removed,
            expirationDate: p.expiration ? p.expiration.date : null,
            expirationTicker: p.expiration && p.expiration.descriptor ? (p.expiration.descriptor.expirationTicker || null) : null,
            strike: p.strike,
            contracts: p.contractsSelling,
            heldContracts: p.heldContracts,
            otmLock: p.otmLock
        })),
        protection: sheet.protectionRows.map(p => ({
            strategyId: p.strategyId, strategyUseAdjacent: p.strategyUseAdjacent, strategyFamilyId: p.strategyFamilyId, strategyType: p.strategyType, strategyLeg: p.strategyLeg,
            strategyGap: p.strategyGap, expectedStrike: p.expectedStrike,
            ticker: p.symbol,
            optionTicker: p.optionTicker,
            displayName: p.displayName,
            optionType: p.optionType,
            side: p.side,
            origin: p.origin,
            removed: p.removed,
            expirationDate: p.expiration ? p.expiration.date : null,
            expirationTicker: p.expiration && p.expiration.descriptor ? (p.expiration.descriptor.expirationTicker || null) : null,
            strike: p.strike,
            contracts: p.contracts,
            otmLock: p.otmLock
        }))
    };
}

/** Parse a saved state; null when absent or unreadable (the caller then loads the combo afresh). */
export function parseSheetState(json: string | null | undefined): PhaSheetState | null {
    if (!json) {
        return null;
    }
    try {
        const s = JSON.parse(json) as PhaSheetState;
        if (!s || typeof s !== 'object' || !Array.isArray(s.underlyings)) {
            return null;
        }
        s.premium = s.premium || [];
        s.protection = s.protection || [];
        [s.premium, s.protection].forEach(rows => {
            const groups: {[id: string]: PhaOptionRowState[]} = {};
            rows.forEach(r => { if (r.strategyId) { (groups[r.strategyId] || (groups[r.strategyId] = [])).push(r); } });
            Object.keys(groups).forEach(id => {
                const group = groups[id], first = group[0], count = first.strategyType === 'Butterfly' ? 3 : 2;
                // Keep malformed Two-for-One facts; the owning sheet/export gate reports them.
                // Removing the type would silently turn covered strategy legs into loose options.
                if (group.some(r => r.strategyType === 'Two-for-One')) { return; }
                const valid = typeof id === 'string' && (first.strategyType === 'Butterfly' || first.strategyType === 'Vertical')
                    && group.length === count && group.every(r => r.ticker === first.ticker && r.optionType === first.optionType
                        && r.strategyType === first.strategyType && Number.isInteger(r.strategyLeg) && r.strategyLeg >= 0 && r.strategyLeg < count
                        && (r.strategyUseAdjacent || r.strategyGap === undefined || r.strategyLeg === 0 || isFinite(r.strategyGap) && r.strategyGap >= 0)
                        && (r.expectedStrike === undefined || isFinite(r.expectedStrike)))
                    && new Set(group.map(r => r.strategyLeg)).size === count;
                if (!valid) {
                    group.forEach(r => { delete r.strategyId; delete r.strategyUseAdjacent; delete r.strategyFamilyId; delete r.strategyType;
                        delete r.strategyLeg; delete r.strategyGap; delete r.expectedStrike; });
                }
            });
        });
        s.sectionSettings = {
            premium: sectionSettings(s.sectionSettings && s.sectionSettings.premium),
            protection: sectionSettings(s.sectionSettings && s.sectionSettings.protection)
        };
        if (s.accountValue === undefined) {
            s.accountValue = s.accountSize === undefined ? null : s.accountSize;
        }
        if (typeof s.stockAllocationPercent !== 'number' || !isFinite(s.stockAllocationPercent)) {
            s.stockAllocationPercent = 100;
        }
        if (typeof s.roundingDeviationPercent !== 'number' || !isFinite(s.roundingDeviationPercent)
            || s.roundingDeviationPercent < 0 || s.roundingDeviationPercent > 100) {
            s.roundingDeviationPercent = 20;
        }
        const valuation = s.accountValuation;
        if (valuation && (typeof valuation.cash !== 'number' || !isFinite(valuation.cash)
            || !Array.isArray(valuation.positions) || valuation.positions.some(p => !p || typeof p.ticker !== 'string'
                || typeof p.quantity !== 'number' || !isFinite(p.quantity) || p.quantity < 0
                || typeof p.unitPrice !== 'number' || !isFinite(p.unitPrice) || p.unitPrice < 0
                || p.pointValue != null && (typeof p.pointValue !== 'number' || !isFinite(p.pointValue) || !(p.pointValue > 0))))) {
            delete s.accountValuation;
        }
        return s;
    } catch (e) {
        console.warn('[pha] saved sheet state could not be read', e);
        return null;
    }
}
